/**
 * Report Actions - Advanced operations (regenerate, add evidence, compare)
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { 
  regenerateReportSections,
  addEvidenceToReport,
  compareReports,
  getReport,
} from '../../api/reports';
import {
  RefreshCw,
  PlusCircle,
  GitCompare,
  AlertCircle,
  CheckCircle,
  Upload,
  FileText,
  FileQuestion,
  Hash,
} from 'lucide-react';

interface SectionDetails {
  index: number;
  name: string;
  wordCount: number;
  citationCount: number;
  hasInsufficientEvidence: boolean;
  confidence: number;
}

interface ReportActionsProps {
  reportId: string;
  evidenceGaps?: number;
  totalSections?: number;
  onRegenerateComplete?: () => void;
}

export function ReportActions({ reportId, evidenceGaps, totalSections, onRegenerateComplete }: ReportActionsProps) {
  const [selectedSections, setSelectedSections] = useState<number[]>([]);
  const [showAddEvidence, setShowAddEvidence] = useState(false);
  const queryClient = useQueryClient();
  
  // Fetch report details to get section metadata
  const { data: report } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    enabled: !!reportId,
  });

  const regenerateMutation = useMutation({
    mutationFn: (sectionIndices: number[]) => regenerateReportSections(reportId, sectionIndices),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      setSelectedSections([]);
      onRegenerateComplete?.();
    },
  });

  const addEvidenceMutation = useMutation({
    mutationFn: (artifactIds: string[]) => addEvidenceToReport(reportId, artifactIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      setShowAddEvidence(false);
    },
  });

  // Parse sections from report metadata
  const getSectionDetails = (): SectionDetails[] => {
    if (!report?.sections) return [];
    
    const sectionsMap = new Map<string, any[]>();
    
    // Group sections by section_name
    report.sections.forEach((section: any) => {
      const sections = sectionsMap.get(section.section_name) || [];
      sections.push(section);
      sectionsMap.set(section.section_name, sections);
    });
    
    // Convert to SectionDetails array
    const details: SectionDetails[] = [];
    let index = 0;
    
    sectionsMap.forEach((sectionQuestions, sectionName) => {
      const totalWords = sectionQuestions.reduce((sum, q) => {
        const words = (q.answer || '').split(/\s+/).filter((w: string) => w.length > 0).length;
        return sum + words;
      }, 0);
      
      // Count actual citations by checking if citations array exists and has items
      const totalCitations = sectionQuestions.reduce((sum, q) => {
        if (!q.citations || !Array.isArray(q.citations)) return sum;
        // Filter out empty or invalid citations
        const validCitations = q.citations.filter((c: any) => c && (c.source || c.text || c.content));
        return sum + validCitations.length;
      }, 0);
      
      const hasInsufficientEvidence = sectionQuestions.some((q: any) => 
        q.status === 'no_evidence' || (q.answer || '').toLowerCase().includes('insufficient evidence')
      );
      
      const avgConfidence = sectionQuestions.reduce((sum, q) => sum + (q.confidence || 0), 0) / sectionQuestions.length;
      
      details.push({
        index,
        name: sectionName,
        wordCount: totalWords,
        citationCount: totalCitations,
        hasInsufficientEvidence,
        confidence: avgConfidence,
      });
      
      index++;
    });
    
    return details;
  };
  
  const sectionDetails = getSectionDetails();

  return (
    <div className="space-y-6">
      {/* Evidence Gaps Warning */}
      {evidenceGaps && evidenceGaps > 0 && (
        <Card>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-900 mb-1">
                  {evidenceGaps} Evidence Gap{evidenceGaps !== 1 ? 's' : ''} Detected
                </h3>
                <p className="text-sm text-yellow-800 mb-3">
                  Some questions couldn't be fully answered due to insufficient evidence in uploaded documents.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => setShowAddEvidence(true)}
                    variant="outline"
                  >
                    <PlusCircle className="h-4 w-4" />
                    Add Evidence Documents
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      // Regenerate all sections with gaps
                      const sectionsWithGaps = Array.from({ length: totalSections || 10 }, (_, i) => i);
                      regenerateMutation.mutate(sectionsWithGaps);
                    }}
                    disabled={regenerateMutation.isPending}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Regenerate with Available Evidence
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Regenerate Sections */}
      <Card title="Regenerate Sections">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select sections to regenerate with updated evidence or improved prompts
          </p>
          
          <div className="space-y-3">
            {sectionDetails.length > 0 ? (
              sectionDetails.map((section) => (
                <label
                  key={section.index}
                  className={`block border-2 rounded-lg cursor-pointer transition-all ${
                    selectedSections.includes(section.index)
                      ? 'border-blue-500 bg-blue-50'
                      : section.hasInsufficientEvidence
                      ? 'border-yellow-300 bg-yellow-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedSections.includes(section.index)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSections([...selectedSections, section.index]);
                          } else {
                            setSelectedSections(selectedSections.filter(s => s !== section.index));
                          }
                        }}
                        className="mt-1 rounded border-gray-300"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            Section {section.index + 1}: {section.name}
                          </span>
                          {section.hasInsufficientEvidence && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                              <AlertCircle className="h-3 w-3" />
                              Insufficient Evidence
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span>{section.wordCount.toLocaleString()} words</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Hash className="h-4 w-4 text-gray-400" />
                            <span>{section.citationCount} citation{section.citationCount !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <CheckCircle className={`h-4 w-4 ${section.confidence >= 70 ? 'text-green-500' : section.confidence >= 50 ? 'text-yellow-500' : 'text-red-500'}`} />
                            <span>{section.confidence.toFixed(0)}% confidence</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              ))
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Array.from({ length: totalSections || 10 }, (_, i) => i + 1).map((sectionNum) => (
                  <label
                    key={sectionNum}
                    className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedSections.includes(sectionNum - 1)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSections.includes(sectionNum - 1)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSections([...selectedSections, sectionNum - 1]);
                        } else {
                          setSelectedSections(selectedSections.filter(s => s !== sectionNum - 1));
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">Section {sectionNum}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <Button
            onClick={() => regenerateMutation.mutate(selectedSections)}
            disabled={selectedSections.length === 0 || regenerateMutation.isPending}
            className="w-full"
          >
            {regenerateMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Regenerating {selectedSections.length} section{selectedSections.length !== 1 ? 's' : ''}...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Regenerate {selectedSections.length > 0 ? `${selectedSections.length} Selected Section${selectedSections.length !== 1 ? 's' : ''}` : 'Sections'}
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Add Evidence Modal */}
      {showAddEvidence && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddEvidence(false)}>
          <div className="max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <Card>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Add Evidence to Address Gaps</h3>
                  <button
                    onClick={() => setShowAddEvidence(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    Upload additional documents containing evidence to fill the gaps, then regenerate affected sections.
                  </p>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload New Evidence Documents
                    </label>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xlsx"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowAddEvidence(false)}>
                      Cancel
                    </Button>
                    <Button onClick={() => {
                      // In production: upload files, get artifact IDs, then call addEvidenceMutation
                      alert('File upload not yet implemented - use Sources page to upload documents');
                    }}>
                      <Upload className="h-4 w-4" />
                      Upload & Add to Report
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

